import styles from "./style.module.scss";
import { Fragment, h } from "preact";
import { useEffect, useReducer, useState } from "preact/hooks";
import { IntlProvider } from "preact-i18n";
import merge from "deepmerge";
import {
    StoreContextWrapper,
    useLoadingStore,
} from "components/StoreContextWrapper/StoreContextWrapper";
import { translations } from "locales";
import ModalWrapper from "components/Modal/Modal";
import Footer from "components/Footer/Footer";
import MainContent from "components/MainContent/MainContent";
import Map from "components/Map/Map";
import AsidePanel from "components/AsidePanel/AsidePanel";
import Summary from "components/Summary/Summary";
import type MvArea from "models/MvArea";
import type MvAddressSelectorSDKOptions from "models/MvAddressSelectorSDKOptions";
import { defaultInputParams } from "services/constants";
import { uuidv4 } from "services/helpers";
import {
    createSDKState,
    sdkReducer,
    SDKAction,
    SDKState,
} from "services/sdkState";
import { postLog } from "services/api";
import { getPricePerAddress } from "services/pricing";
import { MvOptionsErrors } from "models/MvAddressSelectorSDKOptions";

interface ModalAppProps {
    options: MvAddressSelectorSDKOptions;
    optionsError: MvOptionsErrors;
}

const createConfiguration = (options: MvAddressSelectorSDKOptions) => {
    const { selectedAddress, messages, language } = options;
    const baseMessages: Record<string, unknown> =
        (language && translations[language as keyof typeof translations]) ||
        translations.en;
    const countryCode = selectedAddress?.countryCode || "nl";
    const defaults = {
        startRadius: options.startRadius || defaultInputParams.radius!,
        maxRadius: options.maxRadius || (countryCode === "nl" ? 1000 : 400),
    };

    const inputParams = { ...defaultInputParams, radius: defaults.startRadius };
    const areas: MvArea[] = selectedAddress
        ? [
              {
                  id: uuidv4(),
                  inputParams: {
                      ...inputParams,
                      streetName: selectedAddress.streetname,
                      countryCode: selectedAddress.countryCode,
                      postalCode: selectedAddress.postcode,
                      houseNumber: selectedAddress.huisnr,
                      houseNumberExtensionLetter:
                          selectedAddress.huisnr_bag_letter,
                      houseNumberExtensionNumber:
                          selectedAddress.huisnr_bag_toevoeging,
                  },
                  result: null,
                  isSelected: true,
                  disabled: true,
                  addresses: [],
              },
          ]
        : [];
    return {
        initialState: createSDKState(areas, options),
        inputParams,
        defaultOptions: defaults,
        includeInputAdressInSelection: countryCode === "nl",
        showFullZipCode: countryCode === "de",
        definition:
            messages && Object.keys(messages).length > 0
                ? merge(baseMessages, messages)
                : baseMessages,
    };
};

const ModalApp = ({ options, optionsError }: ModalAppProps) => {
    const { isLoading, startLoading, stopLoading, setApiBasePath } =
        useLoadingStore();
    const [configuration] = useState(() => createConfiguration(options));
    const [selectionState, dispatch] = useReducer(
        (state: SDKState, action: SDKAction) =>
            sdkReducer(state, action, options, configuration),
        configuration.initialState,
    );
    const state = {
        ...selectionState,
        pricePerAddress: optionsError
            ? selectionState.pricePerAddress
            : getPricePerAddress(selectionState.totalSelected, options),
    };
    const { definition } = configuration;
    const {
        useModal,
        isModalOpen,
        onModalRequestClose,
        onComplete,
        onCancel,
        apiBasePath,
        containerId,
        addressLimit,
        addressMinimum,
        shouldCloseOnOverlayClick,
        selectedAddress,
        zipCodeAreaMinCount,
    } = options;

    useEffect(() => {
        if (apiBasePath) setApiBasePath(apiBasePath);
    }, [apiBasePath, setApiBasePath]);

    const modalContent = optionsError ? (
        <div className={styles.optionsError}>
            <b>Configuration error</b>:
            <ul>
                {optionsError.map((error, i) => (
                    <li key={i}>{error}</li>
                ))}
            </ul>
        </div>
    ) : (
        <Fragment>
            {state.showSummary && (
                <Summary
                    state={state}
                    onAreaRemove={(area: MvArea) => {
                        dispatch({ type: "removeArea", id: area.id });
                    }}
                    onClose={() => dispatch({ type: "hideSummary" })}
                />
            )}
            <div
                className={`${styles.mvAppWrapper} ${
                    state.showSummary ? styles.hide : ""
                }`}
            >
                <MainContent
                    defaultOptions={configuration.defaultOptions}
                    apiBasePath={apiBasePath}
                    state={state}
                    onAddArea={() =>
                        dispatch({
                            type: "addArea",
                            id: uuidv4(),
                            defaults: configuration.inputParams,
                        })
                    }
                    onSelectArea={(id) =>
                        dispatch({ type: "selectListArea", id })
                    }
                    onRemoveArea={(id) => dispatch({ type: "removeArea", id })}
                    onAddressChange={(id, address) =>
                        dispatch({ type: "setAddress", id, address })
                    }
                    onUpdateParams={(id, patch) =>
                        dispatch({ type: "updateParams", id, patch })
                    }
                    onAddressesFound={(id, addresses) =>
                        dispatch({
                            type: "receiveAddresses",
                            id,
                            addresses,
                        })
                    }
                    onShowAddresses={() =>
                        dispatch({ type: "setPanelOpen", open: true })
                    }
                    countryCode={selectedAddress?.countryCode || "nl"}
                    isLoading={isLoading}
                    startLoading={startLoading}
                    stopLoading={stopLoading}
                />
                <Map
                    apiBasePath={apiBasePath}
                    areas={state.areas}
                    onSelect={(area) =>
                        dispatch({ type: "selectMapArea", id: area.id })
                    }
                    addAddress={(address) =>
                        dispatch({
                            type: "addMapAddress",
                            id: uuidv4(),
                            address,
                            defaults: configuration.inputParams,
                        })
                    }
                    updateAddress={(area, address) =>
                        dispatch({
                            type: "moveMapAddress",
                            id: area.id,
                            address,
                        })
                    }
                    countryCode={selectedAddress?.countryCode || "nl"}
                    isLoading={isLoading}
                    startLoading={startLoading}
                    stopLoading={stopLoading}
                />
            </div>
            <AsidePanel
                isOpen={state.panelOpen}
                isEditable={state.showSummary}
                areas={state.areas}
                showFullZipCode={configuration.showFullZipCode}
                onClose={() => dispatch({ type: "setPanelOpen", open: false })}
                zipCodeAreaMinCount={zipCodeAreaMinCount}
                addressLimit={addressLimit}
                addressMinimum={addressMinimum}
                onConfirm={() => {
                    onComplete?.({
                        result: "confirm_selection",
                        currentSelection: state.selection,
                    });

                    postLog(apiBasePath ?? "", {
                        options: options,
                        areas: state.areas,
                        currentSelection: state.selection,
                    });
                }}
                selectedGroups={state.selectedGroups}
                onGroupsChange={(groups) =>
                    dispatch({ type: "setGroups", groups })
                }
            />
            {!state.showSummary && (
                <Footer
                    showAll={() =>
                        dispatch({ type: "setPanelOpen", open: true })
                    }
                    addresses={state.areas}
                    pricePerAddress={state.pricePerAddress}
                    onClose={() => {
                        onCancel?.({
                            type: "cancel",
                            currentSelection: state.selection,
                        });
                    }}
                    onNext={() => dispatch({ type: "showSummary" })}
                    isLoading={isLoading}
                />
            )}
        </Fragment>
    );

    return (
        <IntlProvider definition={definition}>
            <ModalWrapper
                useModal={useModal}
                apiBasePath={apiBasePath}
                isOpen={isModalOpen ? isModalOpen : false}
                onRequestClose={() =>
                    onModalRequestClose ? onModalRequestClose() : null
                }
                containerId={containerId}
                shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
            >
                {modalContent}
            </ModalWrapper>
        </IntlProvider>
    );
};

const App = ({
    options,
    optionsError,
}: {
    options: MvAddressSelectorSDKOptions;
    optionsError: MvOptionsErrors;
}) => (
    <StoreContextWrapper>
        <ModalApp options={options} optionsError={optionsError} />
    </StoreContextWrapper>
);
export default App;
